/**
 * Route presets for trip planner mode.
 *
 * Keyed by agency scanner, then by facility slug. Each preset defines
 * one or more routes with ordered nights. siteNames are resolved to
 * siteIds at form load time by matching against facility.sites.
 *
 * Route `aliases` serve double duty: they are the single source of truth
 * for route-level search aliases. During seed, the seeder reads these
 * aliases and merges them into the facility's searchText/searchMeta so
 * users can find facilities by route name. Don't duplicate route aliases
 * in parser override files — only define site-type aliases there.
 *
 * Route order matters: the first route is the one the form fills in, and the
 * menu lists the rest in order. Routes are ranked by how many saved
 * trip-planner requests use exactly that route (same sites each night), most
 * saved first, re-ranked from a season of requests in September 2026.
 * Facilities with fewer than 40 saved requests keep their original order
 * until there is enough data.
 *
 * Facility-level flags beside `routes`:
 *   wholeTripOnly — single nights can't be booked on their own (the agency
 *     only sells the full itinerary), so the form hides the "any night"
 *     alert choice and the one-night route, and saves whole-trip requests.
 *   area — the facility covers a whole backcountry area rather than one
 *     trail, so no preset is a safe guess: the form opens on the one-night
 *     route and keeps the presets in the route menu.
 */
export const TRIP_PLANNER_PRESETS = {
  NZDOC: {
    "new-zealand-great-walks-milford-track": {
      // DOC sells the Milford Track only as the full 3-night sequence in one
      // direction, so an alert for one hut night could never be booked.
      wholeTripOnly: true,
      routes: {
        standard: {
          label: "Standard (Glade Wharf → Sandfly Point)",
          description: "Clinton Hut → Mintaro Hut → Dumpling Hut",
          nights: [
            { siteNames: ["Clinton Hut"] },
            { siteNames: ["Mintaro Hut"] },
            { siteNames: ["Dumpling Hut"] },
          ],
        },
      },
    },

    "new-zealand-great-walks-routeburn-track": {
      routes: {
        "routeburn-to-divide-falls-2n": {
          label:
            "Routeburn Shelter → The Divide (2 nights via Routeburn Falls)",
          description: "Routeburn Falls Hut → Lake Mackenzie Hut",
          nights: [
            { siteNames: ["Routeburn Falls Hut"] },
            { siteNames: ["Lake Mackenzie Hut"] },
          ],
        },
        "routeburn-to-divide": {
          label: "Routeburn Shelter → The Divide (3 nights)",
          description: "Routeburn Flats → Routeburn Falls → Lake Mackenzie",
          nights: [
            {
              siteNames: ["Routeburn Flats Hut", "Routeburn Flats Campsite"],
            },
            { siteNames: ["Routeburn Falls Hut"] },
            {
              siteNames: ["Lake Mackenzie Hut", "Lake Mackenzie Campsite"],
            },
          ],
        },
        "divide-to-routeburn-falls-2n": {
          label:
            "The Divide → Routeburn Shelter (2 nights via Routeburn Falls)",
          description: "Lake Mackenzie Hut → Routeburn Falls Hut",
          nights: [
            { siteNames: ["Lake Mackenzie Hut"] },
            { siteNames: ["Routeburn Falls Hut"] },
          ],
        },
        "routeburn-to-divide-2n": {
          label:
            "Routeburn Shelter → The Divide (2 nights via Routeburn Flats)",
          description: "Routeburn Flats → Lake Mackenzie",
          nights: [
            {
              siteNames: ["Routeburn Flats Hut", "Routeburn Flats Campsite"],
            },
            {
              siteNames: ["Lake Mackenzie Hut", "Lake Mackenzie Campsite"],
            },
          ],
        },
        "divide-to-routeburn-2n": {
          label:
            "The Divide → Routeburn Shelter (2 nights via Routeburn Flats)",
          description: "Lake Mackenzie → Routeburn Flats",
          nights: [
            {
              siteNames: ["Lake Mackenzie Hut", "Lake Mackenzie Campsite"],
            },
            {
              siteNames: ["Routeburn Flats Hut", "Routeburn Flats Campsite"],
            },
          ],
        },
        "divide-to-routeburn": {
          label: "The Divide → Routeburn Shelter (3 nights)",
          description: "Lake Mackenzie → Routeburn Falls → Routeburn Flats",
          nights: [
            {
              siteNames: ["Lake Mackenzie Hut", "Lake Mackenzie Campsite"],
            },
            { siteNames: ["Routeburn Falls Hut"] },
            {
              siteNames: ["Routeburn Flats Hut", "Routeburn Flats Campsite"],
            },
          ],
        },
      },
    },

    "new-zealand-great-walks-kepler-track": {
      routes: {
        "clockwise-huts": {
          label: "Clockwise, huts only (3 nights)",
          description: "Luxmore Hut → Iris Burn Hut → Moturau Hut",
          nights: [
            { siteNames: ["Luxmore Hut"] },
            { siteNames: ["Iris Burn Hut"] },
            { siteNames: ["Moturau Hut"] },
          ],
        },
        clockwise: {
          label: "Clockwise, hut or campsite at Iris Burn (3 nights)",
          description: "Luxmore Hut → Iris Burn → Moturau Hut",
          nights: [
            { siteNames: ["Luxmore Hut"] },
            {
              siteNames: ["Iris Burn Hut", "Iris Burn Campsite"],
            },
            { siteNames: ["Moturau Hut"] },
          ],
        },
        counterclockwise: {
          label: "Counter-clockwise (3 nights)",
          description: "Moturau Hut → Iris Burn → Luxmore Hut",
          nights: [
            { siteNames: ["Moturau Hut"] },
            {
              siteNames: ["Iris Burn Hut", "Iris Burn Campsite"],
            },
            { siteNames: ["Luxmore Hut"] },
          ],
        },
        "clockwise-4n": {
          label: "Clockwise (4 nights, inc. Brod Bay)",
          description: "Brod Bay → Luxmore Hut → Iris Burn → Moturau Hut",
          nights: [
            { siteNames: ["Brod Bay Campsite"] },
            { siteNames: ["Luxmore Hut"] },
            {
              siteNames: ["Iris Burn Hut", "Iris Burn Campsite"],
            },
            { siteNames: ["Moturau Hut"] },
          ],
        },
      },
    },

    "new-zealand-great-walks-tongariro-northern-circuit": {
      routes: {
        clockwise: {
          label: "Clockwise from Whakapapa",
          description: "Mangatepopo → Oturere → Waihohonu",
          nights: [
            {
              siteNames: ["Mangatepopo Hut", "Mangatepopo Campsite"],
            },
            {
              siteNames: ["Oturere Hut", "Oturere Campsite"],
            },
            {
              siteNames: ["Waihohonu Hut", "Waihohonu Campsite"],
            },
          ],
        },
        counterclockwise: {
          label: "Counter-clockwise from Whakapapa",
          description: "Waihohonu → Oturere → Mangatepopo",
          nights: [
            {
              siteNames: ["Waihohonu Hut", "Waihohonu Campsite"],
            },
            {
              siteNames: ["Oturere Hut", "Oturere Campsite"],
            },
            {
              siteNames: ["Mangatepopo Hut", "Mangatepopo Campsite"],
            },
          ],
        },
      },
    },

    "new-zealand-great-walks-lake-waikaremoana-track": {
      routes: {
        clockwise: {
          label: "Onepoto → Hopuruahine",
          description: "Panekire Hut → Waiopaoa → Marauiti",
          nights: [
            { siteNames: ["Panekire Hut"] },
            {
              siteNames: ["Waiopaoa Hut", "Waiopaoa Campsite"],
            },
            {
              siteNames: ["Marauiti Hut", "Maraunui Campsite"],
            },
          ],
        },
        counterclockwise: {
          label: "Hopuruahine → Onepoto",
          description: "Waiharuru → Marauiti → Waiopaoa",
          nights: [
            {
              siteNames: ["Waiharuru Hut", "Waiharuru Campsite"],
            },
            {
              siteNames: ["Marauiti Hut", "Maraunui Campsite"],
            },
            {
              siteNames: ["Waiopaoa Hut", "Waiopaoa Campsite"],
            },
          ],
        },
      },
    },

    "new-zealand-great-walks-rakiura-track": {
      routes: {
        clockwise: {
          label: "Clockwise from Lee Bay",
          description: "Port William → North Arm",
          nights: [
            {
              siteNames: ["Port William Hut", "Port William Campsite"],
            },
            {
              siteNames: ["North Arm Hut", "North Arm Campsite"],
            },
          ],
        },
        counterclockwise: {
          label: "Counter-clockwise from Fern Gully",
          description: "North Arm → Port William",
          nights: [
            {
              siteNames: ["North Arm Hut", "North Arm Campsite"],
            },
            {
              siteNames: ["Port William Hut", "Port William Campsite"],
            },
          ],
        },
      },
    },

    "new-zealand-great-walks-paparoa-track": {
      routes: {
        "smoke-ho-to-punakaiki-3n": {
          label: "Smoke-ho → Punakaiki (3 nights)",
          description: "Ces Clark → Moonlight Tops → Pororari",
          nights: [
            { siteNames: ["Ces Clark Hut"] },
            { siteNames: ["Moonlight Tops Hut"] },
            { siteNames: ["Pororari Hut"] },
          ],
        },
        "smoke-ho-to-punakaiki-moonlight-2n": {
          label: "Smoke-ho → Punakaiki (2 nights via Moonlight Tops)",
          description: "Moonlight Tops Hut → Pororari Hut",
          nights: [
            { siteNames: ["Moonlight Tops Hut"] },
            { siteNames: ["Pororari Hut"] },
          ],
        },
        "smoke-ho-to-punakaiki": {
          label: "Smoke-ho → Punakaiki (2 nights via Ces Clark)",
          description: "Ces Clark Hut → Moonlight Tops Hut",
          nights: [
            { siteNames: ["Ces Clark Hut"] },
            { siteNames: ["Moonlight Tops Hut"] },
          ],
        },
        "punakaiki-to-smoke-ho": {
          label: "Punakaiki → Smoke-ho (2 nights)",
          description: "Pororari Hut → Moonlight Tops Hut",
          nights: [
            { siteNames: ["Pororari Hut"] },
            { siteNames: ["Moonlight Tops Hut"] },
          ],
        },
      },
    },

    "new-zealand-great-walks-heaphy-track": {
      routes: {
        "brown-to-kohaihai-4n": {
          label: "Brown Hut → Kohaihai (4 nights)",
          description: "Perry Saddle → Saxon → James Mackay → Heaphy",
          nights: [
            {
              siteNames: ["Perry Saddle Hut", "Perry Saddle Campsite"],
            },
            {
              siteNames: ["Saxon Hut", "Saxon Campsite"],
            },
            {
              siteNames: ["James Mackay Hut", "James Mackay Campsite"],
            },
            {
              siteNames: ["Heaphy Hut", "Heaphy Campsite"],
            },
          ],
        },
        "brown-to-kohaihai-5n": {
          label: "Brown Hut → Kohaihai (5 nights)",
          description:
            "Perry Saddle → Gouland Downs → Saxon → James Mackay → Heaphy",
          nights: [
            {
              siteNames: ["Perry Saddle Hut", "Perry Saddle Campsite"],
            },
            {
              siteNames: ["Gouland Downs Hut", "Gouland Downs Campsite"],
            },
            {
              siteNames: ["Saxon Hut", "Saxon Campsite"],
            },
            {
              siteNames: ["James Mackay Hut", "James Mackay Campsite"],
            },
            {
              siteNames: ["Heaphy Hut", "Heaphy Campsite"],
            },
          ],
        },
        "kohaihai-to-brown-4n": {
          label: "Kohaihai → Brown Hut (4 nights)",
          description: "Heaphy → James Mackay → Saxon → Perry Saddle",
          nights: [
            {
              siteNames: ["Heaphy Hut", "Heaphy Campsite"],
            },
            {
              siteNames: ["James Mackay Hut", "James Mackay Campsite"],
            },
            {
              siteNames: ["Saxon Hut", "Saxon Campsite"],
            },
            {
              siteNames: ["Perry Saddle Hut", "Perry Saddle Campsite"],
            },
          ],
        },
      },
    },

    "new-zealand-great-walks-whanganui-journey": {
      routes: {
        "3-day": {
          label: "3-day (Whakahoro → Pipiriki)",
          description: "John Coull → Tieke Kainga",
          nights: [
            {
              siteNames: ["John Coull Hut", "John Coull Campsite"],
            },
            {
              siteNames: ["Tieke Kainga Hut", "Tieke Campsite"],
            },
          ],
        },
        "5-day": {
          label: "5-day (Taumarunui → Pipiriki)",
          description:
            "Ohinepane → Poukaria → Whakahoro → John Coull → Tieke Kainga",
          nights: [
            { siteNames: ["Ohinepane Campsite"] },
            { siteNames: ["Poukaria Campsite"] },
            {
              siteNames: ["Whakahoro Bunkroom", "Whakahoro Campsite"],
            },
            {
              siteNames: ["John Coull Hut", "John Coull Campsite"],
            },
            {
              siteNames: ["Tieke Kainga Hut", "Tieke Campsite"],
            },
          ],
        },
      },
    },
  },

  ParksCanada: {
    "banff-kootenay-and-yoho-national-park-banff-kootenay-and-yoho-backcountry-all-sites":
      {
        // 62 campgrounds across three parks: most requests here are one
        // night, and only a small share are the Rockwall Trail.
        area: true,
        routes: {
          rockwall: {
            label: "Rockwall Trail (4 nights)",
            aliases: ["Rockwall Trail"],
            description:
              "Helmet Falls → Numa Creek → Tumbling Creek → Floe Lake",
            nights: [
              { siteNames: ["Helmet Falls"] },
              { siteNames: ["Numa Creek"] },
              { siteNames: ["Tumbling Creek"] },
              { siteNames: ["Floe Lake"] },
            ],
          },
        },
      },

    "jasper-national-park-skyline": {
      routes: {
        "2n": {
          label: "Maligne Lake → Signal Mtn (2 nights)",
          aliases: ["Skyline Trail"],
          description: "Snowbowl → Tekarra",
          nights: [{ siteNames: ["Snowbowl"] }, { siteNames: ["Tekarra"] }],
        },
        "4n": {
          label: "Maligne Lake → Signal Mtn (4 nights)",
          description: "Little Shovel → Snowbowl → Curator → Tekarra",
          nights: [
            { siteNames: ["Little Shovel"] },
            { siteNames: ["Snowbowl"] },
            { siteNames: ["Curator"] },
            { siteNames: ["Tekarra"] },
          ],
        },
        "3n": {
          label: "Maligne Lake → Signal Mtn (3 nights)",
          description: "Snowbowl → Curator → Tekarra",
          nights: [
            { siteNames: ["Snowbowl"] },
            { siteNames: ["Curator"] },
            { siteNames: ["Tekarra"] },
          ],
        },
      },
    },

    "jasper-national-park-tonquin-valley": {
      routes: {
        "loop-3n": {
          label: "Tonquin Valley Loop (3 nights)",
          aliases: ["Tonquin Valley"],
          description: "Clitheroe → Amethyst → Maccarib",
          nights: [
            { siteNames: ["Clitheroe"] },
            { siteNames: ["Amethyst"] },
            { siteNames: ["Maccarib"] },
          ],
        },
        "loop-4n": {
          label: "Tonquin Valley Loop (4 nights)",
          description: "Astoria → Clitheroe → Amethyst → Maccarib",
          nights: [
            { siteNames: ["Astoria"] },
            { siteNames: ["Clitheroe"] },
            { siteNames: ["Amethyst"] },
            { siteNames: ["Maccarib"] },
          ],
        },
      },
    },

    "jasper-national-park-brazeau": {
      routes: {
        "loop-4n": {
          label: "Brazeau Loop (4 nights)",
          aliases: ["Brazeau Loop"],
          description: "Four Point → Brazeau Lake → Jonas Cutoff → Four Point",
          nights: [
            { siteNames: ["Four Point"] },
            { siteNames: ["Brazeau Lake"] },
            { siteNames: ["Jonas Cutoff"] },
            { siteNames: ["Four Point"] },
          ],
        },
      },
    },

    "jasper-national-park-maligne-pass": {
      routes: {
        "through-3n": {
          label: "Maligne Pass (3 nights)",
          aliases: ["Maligne Pass"],
          description: "Avalanche → Mary Vaux → Schäffer",
          nights: [
            { siteNames: ["Avalanche"] },
            { siteNames: ["Mary Vaux"] },
            { siteNames: ["Schäffer"] },
          ],
        },
      },
    },
  },

  ParksVictoria: {
    "croajingolong-national-park-the-wilderness-coast-walk": {
      routes: {
        "east-to-west": {
          label: "East to West (3 nights)",
          aliases: ["Wilderness Coast Walk"],
          description: "Sandpatch → Rame Head → Clinton Rocks",
          nights: [
            { siteNames: ["Zone 1 - Sandpatch"] },
            { siteNames: ["Zone 2 - Rame Head"] },
            { siteNames: ["Zone 3 - Clinton Rocks"] },
          ],
        },
        "west-to-east": {
          label: "West to East (3 nights)",
          description: "Clinton Rocks → Rame Head → Sandpatch",
          nights: [
            { siteNames: ["Zone 3 - Clinton Rocks"] },
            { siteNames: ["Zone 2 - Rame Head"] },
            { siteNames: ["Zone 1 - Sandpatch"] },
          ],
        },
      },
    },
  },

  NTParks: {
    "nt-great-walks-jatbula-trail": {
      routes: {
        standard: {
          label: "Jatbula Trail (5 nights)",
          aliases: ["Jatbula Trail"],
          description:
            "Biddlecombe Cascades → Crystal Falls → 17 Mile Falls → Sandy Camp Pool → Sweetwater Pool",
          nights: [
            { siteNames: ["Biddlecombe Cascades Campground"] },
            { siteNames: ["Crystal Falls Campground"] },
            { siteNames: ["17 Mile Falls Campground"] },
            { siteNames: ["Sandy Camp Pool Campground"] },
            { siteNames: ["Sweetwater Pool Campground"] },
          ],
        },
      },
    },
  },

  BCParks: {
    "berg-lake-trail-berg-lake-all-sites": {
      routes: {
        "4n": {
          label: "Berg Lake Trail - Full (4 nights)",
          aliases: ["Berg Lake Trail"],
          description: "Kinney Lake → Whitehorn → Emperor Falls → Berg Lake",
          nights: [
            { siteNames: ["Kinney Lake"] },
            { siteNames: ["Whitehorn"] },
            { siteNames: ["Emperor Falls"] },
            { siteNames: ["Berg Lake"] },
          ],
        },
        "2n": {
          label: "Berg Lake Trail - Short (2 nights)",
          description: "Whitehorn → Berg Lake",
          nights: [{ siteNames: ["Whitehorn"] }, { siteNames: ["Berg Lake"] }],
        },
        "3n": {
          label: "Berg Lake Trail (3 nights)",
          description: "Kinney Lake → Whitehorn → Berg Lake",
          nights: [
            { siteNames: ["Kinney Lake"] },
            { siteNames: ["Whitehorn"] },
            { siteNames: ["Berg Lake"] },
          ],
        },
      },
    },
  },

  GORCAPA: {
    // The Great Ocean Walk's hike-in camps in walking order. GORCAPA
    // publishes the walk east to west, Apollo Bay to the Twelve Apostles,
    // over eight days; each night accepts the camp's individual pads and its
    // group-area pads where the camp has one.
    "great-otway-national-park-great-ocean-walk": {
      routes: {
        "apollo-bay-to-twelve-apostles": {
          label: "Apollo Bay → Twelve Apostles (7 nights)",
          description:
            "Elliott Ridge → Blanket Bay → Cape Otway → Aire River → Johanna Beach → Ryans Den → Devils Kitchen",
          nights: [
            { siteNames: ["Elliott Ridge Hike In", "Elliott Ridge Group"] },
            { siteNames: ["Blanket Bay Hike In", "Blanket Bay Group"] },
            { siteNames: ["Cape Otway Hike In", "Cape Otway Group"] },
            { siteNames: ["Aire River Hike In", "Aire River Group"] },
            { siteNames: ["Johanna Beach Hike In"] },
            { siteNames: ["Ryans Den Hike In"] },
            { siteNames: ["Devils Kitchen Hike In"] },
          ],
        },
      },
    },
  },
};

/**
 * Look up presets for a facility by agency scanner and facility slug.
 */
export function getFacilityPresets(scanner, slug) {
  return TRIP_PLANNER_PRESETS[scanner]?.[slug] || null;
}

/**
 * Resolve preset siteNames to siteIds using the facility's loaded sites.
 */
export function resolvePresetToNights(route, facilitySites) {
  return route.nights.map((night, index) => {
    const siteIds = night.siteNames
      .map((name) => facilitySites.find((s) => s.name === name)?.id)
      .filter(Boolean);
    return { index, siteIds };
  });
}
